import React, { useContext, useEffect } from 'react';
import styles from './EledoUserProfile.module.scss';
import {useForm, FormProvider} from "react-hook-form";
import {showNotyf, useErrorNotyf, useShowMenu} from "hooks";
import ReactTooltip from "react-tooltip";
import {
  EledoMainPageTitle, EledoPendingIcon,
  EledoLoader, EledoError, EledoPendingContent,
  EledoTabs,
  EledoLabel,
} from "components/library";
import classnames from 'classnames';
import { AuthContext } from 'AuthContext';
import { PlatformContext } from 'PlatformContext';
import { useMutation } from '@apollo/client';
import { GENERATE_NEW_API_KEY, GenerateApiKeyData, GenerateApiKeyVars } from 'graphql/AuthQueries';
import NotyfContext from 'context/NotyfContext';
import useCurrentProfile from 'graphql/hooks/useCurrentProfile';
import { EledoProfile, UPDATE_CURRENT_PROFILE, UpdateEledoProfileData, UpdateEledoProfileVars } from 'graphql/EledoProfile';
import { EledoTab } from 'components/library/EledoTabs/EledoTabs';
import EledoUserMembershipsList from 'components/library/EledoUserMembershipsList';

const EledoUserProfile = () => {
  const { loading, error, currentProfile } = useCurrentProfile()
  const [ updateCurrentProfile, { loading: saving, error: savingError } ] = useMutation<UpdateEledoProfileData, UpdateEledoProfileVars>(UPDATE_CURRENT_PROFILE)
  
  const userInfo = currentProfile;
  
  const formHandler = useForm<EledoProfile>({mode: "all"});
  const { formState } = formHandler;
  
  const userChanged = formState.isDirty
  const userValid = formState.isValid

  const platform = useContext(PlatformContext);
  const isEmbedded = platform.isEmbedded;
  
  useErrorNotyf(error || savingError);
  useShowMenu();

  useEffect(() => {
    if(currentProfile !== undefined){
      formHandler.reset()
    }
  }, [formHandler, currentProfile])

  const handleSave = () => {
    if(userValid && userChanged) {
      const profile = formHandler.getValues();

      updateCurrentProfile({ variables: { profile }})
    }
  };

  const renderError = () =>
    <EledoError title={error?.message}
                description={(<React.Fragment>
                  We are sorry &#9785; We could not load your user data. <br/><br/>
                  {(error?.message ? error.message.toLocaleLowerCase() : error) === "network error" && "Please check your internet connection."}
                </React.Fragment>)}/>;

  return (
    <div>
      <div className={styles.EledoUserProfile} data-testid="EledoUserProfile">
        <div className={styles.TopToolbar}>
          <EledoMainPageTitle
            title="Your Profile"
          />
          <div className={classnames("section", "my-section", styles.ButtonDiv)}>
            <span
              data-tip={userValid ? userChanged ? "" : "Your profile is saved" : "Please check the validity of your input data"}
              data-for="save-invalid-tooltip">
              <button className="button w-button"
                      onClick={handleSave}
                      {...(userChanged && userValid ? {} : {"aria-disabled": "true"})}>
                { saving ?
                    <React.Fragment>
                      <EledoPendingIcon/> Saving...
                    </React.Fragment> :
                    <React.Fragment>
                      <i className="icon-floppy-disk"/> Save
                    </React.Fragment>
                }
              </button>
            </span>
          </div>

          <ReactTooltip
            id="save-invalid-tooltip"
            place="bottom"
            type="dark"
            effect="float"
            multiline={true}
          />
        </div>

        <div className="stretch-vertically">
          <FormProvider {...formHandler}>
            <form>
              <EledoTabs id="userProfileTabs">
                <EledoTab label="General" path="">
                  {!error && !loading && <TabGeneral userInfo={userInfo} />}
                </EledoTab>
                <EledoTab label="API" path="api">
                  {!error && !loading && !isEmbedded && <TabAPI/>}
                </EledoTab>
              </EledoTabs>
              { loading && <div className="flex-center"><EledoLoader/></div>}
              { error && renderError() }
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
}

const TabGeneral = ({ userInfo }: {userInfo: EledoProfile | undefined}) => {
  const auth = useContext(AuthContext)
  if(userInfo === undefined){
    return (<></>)
  }

  return (
    <div>
      <fieldset>
        <EledoLabel id="login" label="User"/>
        <div>{auth.user?.email}</div>
      </fieldset>
      <h3>Account Memberships</h3>
      <EledoUserMembershipsList />
    </div>
  );
};

const TabAPI = () => {
  const auth = useContext(AuthContext);
  const userData = auth.user;
  const notyf = useContext(NotyfContext);

  const [generateNewApiKey, { loading }] = useMutation<GenerateApiKeyData, GenerateApiKeyVars>(GENERATE_NEW_API_KEY, {
    update(proxy, { data }) {
      if(data){
        auth.newApiKey(data.regenApiKey.apiKey);

        showNotyf("Api Key has been generated and replaced.", notyf, "success");
      }
    },
    onError(error){
      showNotyf(error, notyf, "error");
    }
  });

  return (
    <div>
      { userData &&
        <fieldset>
          <EledoLabel id="apiKey" label="API Key"/>
          <input id="apiKey" style={{cursor: "initial"}}
                 value={auth.apiToken}
                 disabled={true}/>
        </fieldset>
      }
      <button className="button w-button outline"
              onClick={e => {e.preventDefault();generateNewApiKey()}}>
        <EledoPendingContent state={loading}
                             content="Generate new API Key"
                             pendingContent="Generating..."/>
      </button>
    </div>
  );
}

export default EledoUserProfile;

